/**
 * Fuel Account Form component.
 *
 * Shows/hides conditional fields based on the selected fuel account type
 * (Account, Overseas Account, Non-Account). Emits a custom 'fuel-account-change'
 * event when values change.
 *
 * Exports pure helper functions for testability.
 *
 * @module fuel-account-form
 * Requirements: 11.1, 11.2, 11.3, 11.4
 */

import { FUEL_ACCOUNT_TYPES } from '../utils/constants.js';
import { validateFuelAccountPayload } from '../utils/validation.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Returns the set of required fields for a given fuel account type.
 *
 * @param {string} type - One of FUEL_ACCOUNT_TYPES
 * @returns {{ litres: boolean, cost: boolean, totalCost: boolean, currency: string }}
 */
export function getRequiredFields(type) {
  switch (type) {
    case 'ACCOUNT':
      return { litres: true, cost: true, totalCost: false, currency: 'GBP' };
    case 'OVERSEAS_ACCOUNT':
      return { litres: true, cost: true, totalCost: false, currency: 'EUR' };
    case 'NON_ACCOUNT':
      return { litres: true, cost: false, totalCost: true, currency: 'GBP' };
    default:
      return { litres: false, cost: false, totalCost: false, currency: '' };
  }
}

/**
 * Returns the cost label for a given fuel account type.
 *
 * @param {string} type - One of FUEL_ACCOUNT_TYPES
 * @returns {string}
 */
export function getCostLabel(type) {
  switch (type) {
    case 'ACCOUNT':
      return 'Cost (GBP)';
    case 'OVERSEAS_ACCOUNT':
      return 'Cost (EUR)';
    case 'NON_ACCOUNT':
      return 'Total Cost (GBP)';
    default:
      return 'Cost';
  }
}

// ---------------------------------------------------------------------------
// Display labels for fuel account types
// ---------------------------------------------------------------------------

const TYPE_LABELS = {
  ACCOUNT: 'Account',
  OVERSEAS_ACCOUNT: 'Overseas Account',
  NON_ACCOUNT: 'Non-Account',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Creates a Fuel Account Form component with conditional fields.
 *
 * @param {Object} [config={}]
 * @param {string} [config.initialType='ACCOUNT'] - Initial fuel account type
 * @param {function} [config.onChange] - Callback when values change: (payload) => void
 * @returns {HTMLElement}
 */
export function createFuelAccountForm({ initialType, onChange } = {}) {
  let currentType = FUEL_ACCOUNT_TYPES.includes(initialType) ? initialType : 'ACCOUNT';

  const container = document.createElement('div');
  container.className = 'fuel-account-form';

  // Title
  const title = document.createElement('h4');
  title.className = 'fuel-account-form__title';
  title.textContent = 'Fuel Uplift';
  container.appendChild(title);

  // Type selector
  const typeRow = document.createElement('div');
  typeRow.className = 'fuel-account-form__type-row';

  const typeLabel = document.createElement('label');
  typeLabel.className = 'fuel-account-form__type-label';
  typeLabel.textContent = 'Fuel Account Type';
  typeRow.appendChild(typeLabel);

  const typeSelect = document.createElement('select');
  typeSelect.className = 'fuel-account-form__type-select';
  typeSelect.setAttribute('aria-label', 'Fuel account type');
  for (const t of FUEL_ACCOUNT_TYPES) {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = TYPE_LABELS[t] || t;
    if (t === currentType) opt.selected = true;
    typeSelect.appendChild(opt);
  }
  typeRow.appendChild(typeSelect);
  container.appendChild(typeRow);

  // Litres + Cost inline row
  const litresCostRow = document.createElement('div');
  litresCostRow.className = 'fuel-account-form__inline-row';

  // Litres input
  const litresRow = createFieldRow('fuel-account-form__litres', 'Uplift (litres)', 'number');
  litresCostRow.appendChild(litresRow.row);

  // Cost input (shown for ACCOUNT and OVERSEAS_ACCOUNT)
  const costRow = createFieldRow('fuel-account-form__cost', getCostLabel(currentType), 'number');
  litresCostRow.appendChild(costRow.row);

  // Total cost input (shown for NON_ACCOUNT — replaces cost in the same row)
  const totalCostRow = createFieldRow('fuel-account-form__total-cost', 'Total Cost (GBP)', 'number');
  litresCostRow.appendChild(totalCostRow.row);

  container.appendChild(litresCostRow);

  // Message area
  const messageEl = document.createElement('div');
  messageEl.className = 'fuel-account-form__message';
  messageEl.setAttribute('role', 'status');
  container.appendChild(messageEl);

  // --- Field visibility ---

  function updateFieldVisibility() {
    const fields = getRequiredFields(currentType);
    litresRow.row.style.display = fields.litres ? '' : 'none';
    costRow.row.style.display = fields.cost ? '' : 'none';
    totalCostRow.row.style.display = fields.totalCost ? '' : 'none';
    costRow.label.textContent = getCostLabel(currentType);
  }

  function getPayload() {
    const payload = { type: currentType };
    const litres = parseFloat(litresRow.input.value);
    if (!Number.isNaN(litres)) payload.litres = litres;

    if (currentType === 'ACCOUNT') {
      const cost = parseFloat(costRow.input.value);
      if (!Number.isNaN(cost)) payload.cost = cost;
      payload.currency = 'GBP';
    } else if (currentType === 'OVERSEAS_ACCOUNT') {
      const cost = parseFloat(costRow.input.value);
      if (!Number.isNaN(cost)) payload.cost = cost;
      payload.currency = 'EUR';
    } else if (currentType === 'NON_ACCOUNT') {
      const totalCost = parseFloat(totalCostRow.input.value);
      if (!Number.isNaN(totalCost)) payload.totalCost = totalCost;
      payload.currency = 'GBP';
    }

    return payload;
  }

  function notifyChange() {
    const payload = getPayload();
    const validation = validateFuelAccountPayload(payload);
    messageEl.textContent = validation.valid ? '' : validation.errors[0] || '';

    container.dispatchEvent(new CustomEvent('fuel-account-change', {
      bubbles: true,
      detail: { payload, valid: validation.valid },
    }));

    if (typeof onChange === 'function') {
      onChange(payload, validation.valid);
    }
  }

  // --- Event handlers ---

  typeSelect.addEventListener('change', () => {
    currentType = typeSelect.value;
    updateFieldVisibility();
    notifyChange();
  });

  litresRow.input.addEventListener('input', notifyChange);
  costRow.input.addEventListener('input', notifyChange);
  totalCostRow.input.addEventListener('input', notifyChange);

  // Initial render
  updateFieldVisibility();

  /** Get the current payload. */
  container.getPayload = getPayload;

  /** Get the current fuel account type. */
  container.getType = () => currentType;

  return container;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Creates a labelled input field row.
 * @param {string} className - CSS class prefix
 * @param {string} labelText - Label text
 * @param {string} inputType - Input type attribute
 * @returns {{ row: HTMLElement, label: HTMLElement, input: HTMLInputElement }}
 */
function createFieldRow(className, labelText, inputType) {
  const row = document.createElement('div');
  row.className = `${className}-row`;

  const label = document.createElement('label');
  label.className = `${className}-label`;
  label.textContent = labelText;
  row.appendChild(label);

  const input = document.createElement('input');
  input.type = inputType;
  input.className = `${className}-input`;
  input.setAttribute('aria-label', labelText);
  if (inputType === 'number') {
    input.min = '0.01';
    input.step = '0.01';
  }
  row.appendChild(input);

  return { row, label, input };
}
